import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { IntuiSeparator } from '@/components/intueri/IntuiSeparator'

/**
 * Figma (node 18684:15220 — Field):
 *
 * Composition: Label + Input + Description
 * Orientation: Vertical (stacked) or Responsive (side-by-side)
 * Description placement: Under Input (default) or Under Label
 * Invalid: label turns text-destructive
 *
 * Specs:
 *   Vertical:   flex flex-col gap-2
 *   Responsive: flex gap-2 items-start (label+desc left, input right)
 *   Label:      text-sm font-medium text-foreground | text-destructive
 *   Description: text-sm text-muted-foreground
 *   Link:       text-sm text-foreground, absolute top-right
 */

export interface IntuiFieldProps {
  /** Label text */
  label?: string
  /** Description / helper text */
  description?: string
  /** Where to place the description */
  descriptionPlacement?: 'under-input' | 'under-label'
  /** Layout orientation */
  orientation?: 'vertical' | 'responsive'
  /** Invalid state — label turns red */
  invalid?: boolean
  /** Optional link in the label row (e.g. "Forgot your password?") */
  link?: { text: string; href?: string; onClick?: () => void }
  /** Show required asterisk on label */
  required?: boolean
  /** HTML for attribute to connect label to input */
  htmlFor?: string
  /** The input element(s) */
  children: ReactNode
  className?: string
}

function IntuiField({
  label,
  description,
  descriptionPlacement = 'under-input',
  orientation = 'vertical',
  invalid,
  link,
  required,
  htmlFor,
  children,
  className,
}: IntuiFieldProps) {
  const descriptionEl = description && (
    <p className="text-sm text-muted-foreground">{description}</p>
  )

  const labelEl = label && (
    <div className="flex items-center justify-between">
      <label
        htmlFor={htmlFor}
        className={cn(
          'text-sm font-medium leading-none',
          invalid ? 'text-destructive' : 'text-foreground',
        )}
      >
        {label}
        {required && <span className="text-destructive ml-0.5" aria-hidden="true">*</span>}
      </label>
      {link && (link.href ? (
        <a
          href={link.href}
          onClick={link.onClick}
          className="text-sm text-foreground hover:underline cursor-pointer"
        >
          {link.text}
        </a>
      ) : (
        <button
          type="button"
          onClick={link.onClick}
          className="text-sm text-foreground hover:underline cursor-pointer"
        >
          {link.text}
        </button>
      ))}
    </div>
  )

  if (orientation === 'responsive') {
    return (
      <div className={cn('flex gap-2 items-start', className)}>
        <div className="flex-1 flex flex-col gap-1.5">
          {labelEl}
          {descriptionPlacement === 'under-label' && descriptionEl}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          {children}
          {descriptionPlacement === 'under-input' && descriptionEl}
        </div>
      </div>
    )
  }

  // Vertical (default)
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {labelEl}
      {descriptionPlacement === 'under-label' && descriptionEl}
      {children}
      {descriptionPlacement === 'under-input' && descriptionEl}
    </div>
  )
}

/* ─── Field Group ─── */
export interface IntuiFieldGroupProps {
  orientation?: 'horizontal' | 'vertical'
  children: ReactNode
  className?: string
}

function IntuiFieldGroup({ orientation = 'horizontal', children, className }: IntuiFieldGroupProps) {
  return (
    <div className={cn(
      orientation === 'horizontal' ? 'flex gap-4' : 'flex flex-col gap-4',
      className,
    )}>
      {children}
    </div>
  )
}

/* ─── Field Legend ─── */
export interface IntuiFieldLegendProps {
  /** Section title */
  title: string
  /** Optional description below title */
  description?: string
  className?: string
}

function IntuiFieldLegend({ title, description, className }: IntuiFieldLegendProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <p className="text-sm font-semibold text-foreground">{title}</p>
      {description && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
    </div>
  )
}

/* ─── Field Separator ─── */
function IntuiFieldSeparator({ className }: { className?: string }) {
  return <IntuiSeparator className={className} />
}

/* ─── Field Buttons ─── */
export interface IntuiFieldButtonsProps {
  orientation?: 'horizontal' | 'vertical'
  children: ReactNode
  className?: string
}

function IntuiFieldButtons({ orientation = 'horizontal', children, className }: IntuiFieldButtonsProps) {
  return (
    <div className={cn(
      orientation === 'horizontal' ? 'flex gap-2' : 'flex flex-col gap-2',
      className,
    )}>
      {children}
    </div>
  )
}

export {
  IntuiField,
  IntuiFieldGroup,
  IntuiFieldLegend,
  IntuiFieldSeparator,
  IntuiFieldButtons,
}
